import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";

const EMPTY_FORM = { name: "", quantity: 1, service_ids: [] };

export default function Resources() {
  const [resources, setResources] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = () =>
    api
      .get("/owner/resources")
      .then(({ data }) => setResources(data))
      .catch(() => setResources([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/owner/services").then(({ data }) => setServices(data)).catch(() => setServices([]));
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const startEdit = (resource) => {
    setEditingId(resource.id);
    setForm({ name: resource.name, quantity: resource.quantity, service_ids: resource.services?.map((s) => s.id) || [] });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      service_ids: f.service_ids.includes(id) ? f.service_ids.filter((s) => s !== id) : [...f.service_ids, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/owner/resources/${editingId}`, form);
      } else {
        await api.post("/owner/resources", form);
      }
      cancelForm();
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/owner/resources/${id}`);
    load();
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">Resources</h1>
        <Button size="sm" onClick={() => (showForm ? cancelForm() : startCreate())}>
          <Plus className="h-4 w-4" /> Add resource
        </Button>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Rooms or equipment needed for specific services — bookings are capped so the resource is never
        double-booked.
      </p>

      {showForm && (
        <Card className="mb-6 p-4">
          <p className="mb-3 text-sm font-semibold text-slate-900">{editingId ? "Edit resource" : "New resource"}</p>
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input placeholder="Resource name (e.g. Treatment Room 1)" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input placeholder="Quantity available" type="number" min={1} required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            <div className="sm:col-span-2">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Used by services</p>
              <div className="flex flex-wrap gap-2">
                {services.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => toggleService(s.id)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                      form.service_ids.includes(s.id) ? "border-primary bg-primary/5 text-primary" : "border-border text-slate-600"
                    }`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" disabled={saving}>{saving ? "Saving…" : editingId ? "Save changes" : "Save resource"}</Button>
              <Button type="button" variant="outline" onClick={cancelForm}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : resources.length === 0 ? (
        <p className="text-muted-foreground">No resources yet — add rooms or equipment above.</p>
      ) : (
        <div className="divide-y divide-border rounded-2xl border border-border">
          {resources.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <p className="font-medium text-slate-900">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.services?.map((s) => s.name).join(", ") || "Not linked to a service"}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Badge>{r.quantity} available</Badge>
                <button onClick={() => startEdit(r)} className="text-muted-foreground hover:text-primary">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
